import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Checkbox } from "@/components/ui/checkbox";
import { useAuth } from "@/context/AuthContext";
import { api, apiError } from "@/lib/api";
import AuthShell from "@/pages/portal/AuthShell";
import { Field, ErrorText, Panel, btnPrimary, inputCls } from "@/components/portal/ui";

const BASE = { first_name: "", last_name: "", email: "", password: "", phone: "", city: "", license_number: "" };

export default function RegisterPage({ kind }) {
  const isDoctor = kind === "doctor";
  const { registerDoctor, registerNurse } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ ...BASE, specialty_ids: [], bio: "" });
  const [specialties, setSpecialties] = useState([]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isDoctor) api.get("/specialties").then((r) => setSpecialties(r.data.filter((s) => s.active))).catch(() => {});
  }, [isDoctor]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const toggleSpec = (id, on) => setForm((f) => ({ ...f, specialty_ids: on ? [...f.specialty_ids, id] : f.specialty_ids.filter((x) => x !== id) }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (isDoctor && form.specialty_ids.length === 0) {
      setError("Seleziona almeno una specialità.");
      return;
    }
    setLoading(true);
    try {
      const payload = { ...form, email: form.email.trim() };
      if (!isDoctor) {
        delete payload.specialty_ids;
        delete payload.bio;
      }
      await (isDoctor ? registerDoctor(payload) : registerNurse(payload));
      navigate(isDoctor ? "/medico" : "/infermiere", { replace: true });
    } catch (err) {
      setError(apiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell
      title={isDoctor ? "Registrazione medico" : "Registrazione infermiere"}
      subtitle={isDoctor ? "Crea il tuo profilo: potrai impostare i tuoi orari di disponibilità e modificarli quando vuoi. Il team TytoCare valuterà la candidatura." : "Crea il tuo profilo per entrare nella rete territoriale TytoCare. Il team valuterà la candidatura."}
      aside={{
        title: isDoctor ? "Gli orari li decidi tu." : "Porta la visita a casa dei pazienti.",
        text: isDoctor ? "Visite assistite a distanza con un infermiere accanto al paziente. Imposta il tuo calendario e aggiornalo in qualsiasi momento." : "Raggiungi il paziente con il kit TytoCare mentre il medico visita in diretta.",
      }}
    >
      <Panel>
        <form onSubmit={submit} className="grid grid-cols-1 gap-4 sm:grid-cols-2" data-testid={`register-${kind}-form`}>
          <Field label="Nome" htmlFor="reg-first"><input id="reg-first" required value={form.first_name} onChange={set("first_name")} className={inputCls} data-testid="register-first-name-input" /></Field>
          <Field label="Cognome" htmlFor="reg-last"><input id="reg-last" required value={form.last_name} onChange={set("last_name")} className={inputCls} data-testid="register-last-name-input" /></Field>
          <Field label="Email" htmlFor="reg-email"><input id="reg-email" type="email" required autoComplete="email" value={form.email} onChange={set("email")} className={inputCls} data-testid="register-email-input" /></Field>
          <Field label="Password" htmlFor="reg-password" hint="Minimo 8 caratteri"><input id="reg-password" type="password" required minLength={8} autoComplete="new-password" value={form.password} onChange={set("password")} className={inputCls} data-testid="register-password-input" /></Field>
          <Field label="Telefono" htmlFor="reg-phone"><input id="reg-phone" required value={form.phone} onChange={set("phone")} className={inputCls} placeholder="+39 …" data-testid="register-phone-input" /></Field>
          <Field label="Città / zona operativa" htmlFor="reg-city"><input id="reg-city" value={form.city} onChange={set("city")} className={inputCls} placeholder="Roma" data-testid="register-city-input" /></Field>
          <Field label={isDoctor ? "N. iscrizione Ordine dei Medici" : "N. iscrizione OPI"} htmlFor="reg-license" className="sm:col-span-2">
            <input id="reg-license" value={form.license_number} onChange={set("license_number")} className={inputCls} data-testid="register-license-input" />
          </Field>

          {isDoctor && (
            <>
              <Field label="Specialità" className="sm:col-span-2" hint="Puoi selezionarne più di una">
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-2" data-testid="register-specialties">
                  {specialties.map((s) => (
                    <label key={s.id} className="flex items-center gap-2 rounded-2xl bg-[#F6F4FA] px-3 py-2 text-sm cursor-pointer">
                      <Checkbox checked={form.specialty_ids.includes(s.id)} onCheckedChange={(v) => toggleSpec(s.id, !!v)} className="data-[state=checked]:bg-[#5B2C91] data-[state=checked]:border-[#5B2C91]" data-testid={`register-specialty-${s.id}`} />
                      {s.name}
                    </label>
                  ))}
                </div>
              </Field>
              <Field label="Breve presentazione (facoltativa)" htmlFor="reg-bio" className="sm:col-span-2">
                <textarea id="reg-bio" rows={3} value={form.bio} onChange={set("bio")} className={inputCls} data-testid="register-bio-input" />
              </Field>
            </>
          )}

          <div className="sm:col-span-2"><ErrorText testid="register-error">{error}</ErrorText></div>
          <button type="submit" disabled={loading} className={`${btnPrimary} w-full !py-3 sm:col-span-2`} data-testid="register-submit-btn">
            {loading ? "Invio in corso…" : "Invia candidatura e crea profilo"}
          </button>
        </form>
      </Panel>
      <p className="mt-6 text-center text-sm text-[#6B6280]">
        Hai già un account? <Link to="/area-riservata" className="font-semibold text-[#5B2C91] hover:underline" data-testid="register-login-link">Accedi</Link>
      </p>
    </AuthShell>
  );
}
